import React, { Fragment, useState, useEffect, useContext } from "react";
import { Breadcrumbs, Btn } from "../../AbstractElements";
import SearchTable from "../Common/Component/SearchTable";
import { Plus } from "react-feather";
import OfferCards from "./OfferCards";
import OfferDialog from "./OfferDialog";
import {
  getOffersList,
  createOffer,
  deleteOffer,
  updateOffer,
} from "../../Services/Offer.service";
import { showAlert } from "../../Services/Shared.service";
import SharedDataContext from "../../_helper/SharedData";

const PAGE_SIZE = 6;

const Offer = () => {
  const { globalCityField } = useContext(SharedDataContext);
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [searchData, setSearchData] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  // Dummy state for SearchTable props compatibility
  const [filterData, setFilterData] = useState([]);
  const [filterType, setFilterType] = useState("");

  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchOffers = () => {
    setIsLoading(true);
    getOffersList({ page: currentPage, size: PAGE_SIZE })
      .then((res) => {
        if (res.data && res.data.list) {
          setOffers(res.data.list);
          setFilteredOffers(res.data.list);
          const count = res.data.count || 0;
          setTotalPages(Math.ceil(count / PAGE_SIZE));
        }
      })
      .catch((err) => {
        console.error("Error fetching offers", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchOffers();
  }, [currentPage]);

  useEffect(() => {
    if (searchData) {
      const lowerSearch = searchData.toLowerCase();
      const filtered = offers.filter(
        (offer) =>
          (offer.offerName || "").toLowerCase().includes(lowerSearch) ||
          (offer.offerCode || "").toLowerCase().includes(lowerSearch),
      );
      setFilteredOffers(filtered);
    } else {
      setFilteredOffers(offers);
    }
  }, [searchData, offers]);

  const handleAddClick = () => {
    setEditingOffer(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (offer) => {
    setEditingOffer(offer);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (offerId) => {
    deleteOffer(offerId)
      .then((res) => {
        if (res.status === 200) {
          showAlert("success", "Offer deleted successfully");
          fetchOffers();
        } else {
          showAlert("error", "Failed to delete offer");
        }
      })
      .catch((err) => {
        console.error("Error deleting offer", err);
        showAlert("error", "Failed to delete offer");
      });
  };

  const handleSave = (data) => {
    const apiCall = editingOffer ? updateOffer : createOffer;
    return apiCall(data)
      .then((res) => {
        if (res.status === 200) {
          showAlert(
            "success",
            editingOffer
              ? "Offer updated successfully"
              : "Offer created successfully",
          );
          fetchOffers();
          return true;
        } else {
          showAlert(
            "error",
            editingOffer ? "Failed to update offer" : "Failed to create offer",
          );
          return false;
        }
      })
      .catch((err) => {
        console.error("Error saving offer", err);
        const errorMsg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save offer";
        showAlert("error", errorMsg);
        return false;
      });
  };

  const handleStatusChange = (offer) => {
    const updatedOffer = { ...offer, isActive: !offer.isActive };
    updateOffer(updatedOffer)
      .then((res) => {
        if (res.status === 200) {
          showAlert(
            "success",
            `Offer ${!offer.isActive ? "activated" : "deactivated"} successfully`,
          );
          fetchOffers();
        } else {
          showAlert("error", "Failed to update status");
        }
      })
      .catch((err) => {
        console.error("Error updating offer status", err);
        showAlert("error", "Failed to update status");
      });
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page - 1); // MUI Pagination is 1-indexed, our state is 0-indexed
  };

  return (
    <Fragment>
      {globalCityField?.value?.isOfferEnabled == true ||
      globalCityField?.value?.id == null ? (
        <>
          <Breadcrumbs
            parent="Configuration"
            subParent="Offer"
            mainTitle="Offer Configuration"
            span2={
              <Btn
                attrBtn={{
                  style: {
                    display: "flex",
                    padding: "7px 14px",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  onClick: handleAddClick,
                }}
              >
                <Plus size={"18px"} style={{ marginRight: "5px" }} /> Add
              </Btn>
            }
            span3={
              <SearchTable
                searchData={searchData}
                setSearchData={setSearchData}
                setFilterData={setFilterData}
                setFilterType={setFilterType}
              />
            }
          />

          <div className="container-fluid">
            <OfferCards
              tableData={filteredOffers}
              onStatusChange={handleStatusChange}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div
          className="container-fluid"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <div className="card">
            <div className="card-body text-center">
              <h4>Offer is not available for this city</h4>
            </div>
          </div>
        </div>
      )}

      <OfferDialog
        isOpen={isDialogOpen}
        toggler={() => setIsDialogOpen(!isDialogOpen)}
        offerObj={editingOffer}
        onSave={handleSave}
      />
    </Fragment>
  );
};

export default Offer;
